import React, { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useDebounce } from '@node-space/hooks'
import { StatusCard } from '@node-space/storybook-components/dist/StatusCard'
import { TextAction } from '@node-space/storybook-components/dist/TextAction'
import { Avatar, Box, Button, Combobox, Illustration, Input } from '@node-space/ui-components'
import { Description } from 'components/text/Description'
import { PathNames } from 'constants/General'
import { BeneficiaryFilters, useGetBeneficiaries } from 'hooks/fiat/useFiatBeneficiaries'
import { useWalletQuery } from 'hooks/queries/useWalletQuery'
import { useWalletsQueryV2 } from 'hooks/queries/useWalletsQueryV2'
import { useGetWalletLegacyId } from 'hooks/queries/Wallets/useGetWalletLegacyId'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { BeneficiaryDetails } from 'types/beneficiaries'
import { SourceWalletTypes } from 'types/move/move'
import { WalletType } from 'types/types'
import { FiatSendStep, FiatSendSteps } from 'types/walletActions'
import { WalletTypes } from 'types/wallets'
import { getFiatSendBeneficiaryFilters } from 'utils/beneficiaries'
import { floorWithPrecision } from 'utils/utils'
import {
  fiatSendFormAtom,
  selectedFiatBeneficiaryAtom,
  selectedWalletAtom,
} from '../../Pay/atoms/atoms'
import { SourcePayWallet } from '../../Pay/types'
import useBeneficiaryFormContext from '../hooks/useBeneficiaryFormContext'
import { useWalletBeneficiaryFilters } from '../hooks/useWalletBeneficiaryFilters'
import BeneficiaryContent from './BeneficiaryContent'

interface BeneficiaryFormProps {
  setStep: React.Dispatch<React.SetStateAction<FiatSendStep>>
}

const BeneficiaryForm = ({ setStep }: BeneficiaryFormProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [walletAction] = useContext(WalletActionContext)

  const [sourceWalletAtom, setSourceWalletAtom] = useAtom(selectedWalletAtom)
  const setBeneficiaryAtom = useSetAtom(selectedFiatBeneficiaryAtom)
  const formAtom = useAtomValue(fiatSendFormAtom)

  const [showErrorState, _setShowErrorState] = useState(false)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [beneficiaryFilters, setBeneficiaryFilters] = useState<BeneficiaryFilters>()
  const debouncedSearchTerm: string = useDebounce(searchQuery, 1000)

  const { errors, clearErrors, register, setValue, setError, resetField } = formAtom || {}

  const { data: wallets, isFetching: isFetchingWallets } = useWalletsQueryV2({
    walletType: WalletTypes.FIAT,
    max: 600,
    order: 'asc',
    sort: 'description',
  })

  const { data: customerWalletLegacyId, isFetching: isFetchingCustomerWalletLegacyId } =
    useGetWalletLegacyId(walletAction?.meta?.customerWallet?.id)

  const { data: customerWallet, isFetching: isFetchingCustomerWallet } = useWalletQuery(
    customerWalletLegacyId,
    !!customerWalletLegacyId
  )

  const { isLoading: isFetchingWalletFilters, filters } = useWalletBeneficiaryFilters({
    wallet: sourceWalletAtom,
  })

  const { activeWallets, isInsufficientBalance, isPayoutsSupported, sourceWalletOptions } =
    useBeneficiaryFormContext({
      wallet: sourceWalletAtom,
      wallets: wallets?.data,
      filters,
    })

  const walletsOptions = useMemo(() => {
    if (!customerWallet) {
      return sourceWalletOptions
    }

    if (customerWallet) {
      return [
        {
          LeftElement: <Avatar name={customerWallet?.currency?.code?.toLowerCase()} size="small" />,
          value: customerWallet?.lsid,
          leftText: customerWallet?.description,
          rightText: `${floorWithPrecision(customerWallet?.balance ?? 0, customerWallet?.currency?.quantityPrecision)} ${customerWallet?.currency?.code}`,
        },
      ]
    }

    return []
  }, [sourceWalletAtom, activeWallets, customerWallet])

  const { fiatBeneficiaries, isFetching: isFetchingBeneficiaries } =
    useGetBeneficiaries(beneficiaryFilters)

  useEffect(() => {
    resetField('amount')
    resetField('fee')
    resetField('paymentReference')
    resetField('beneficiaryReference')
  }, [])

  useEffect(() => {
    if (isFetchingCustomerWallet) return

    if (customerWallet && !sourceWalletAtom?.walletId) {
      const sourceWallet = {
        walletId: customerWallet?.lsid,
        legacyWalletId: customerWallet?.id,
        walletName: customerWallet?.description,
        currency: customerWallet?.currency?.code,
        balance: customerWallet?.available,
        walletType: SourceWalletTypes.CUSTOMER,
        supportedTransferDestinations: customerWallet?.supportedTransferDestinations,
        supportsInternalBvnkNetworkTransfers: customerWallet?.supportsInternalBvnkNetworkTransfers,
        supportsFirstPartyPayouts: customerWallet?.supportsWithdrawals,
        supportsThirdPartyPayouts: customerWallet?.supportsThirdParty,
        supportsPayins: customerWallet?.supportsDeposits,
      } as SourcePayWallet

      setSourceWalletAtom(sourceWallet)

      return
    }

    if (
      !sourceWalletAtom?.walletId &&
      walletAction?.sourceWalletReference &&
      activeWallets?.length
    ) {
      const selectedWallet = activeWallets?.find(
        wallet => wallet?.lsid === walletAction?.sourceWalletReference
      ) as WalletType

      const sourceWallet = {
        walletId: selectedWallet?.lsid,
        legacyWalletId: selectedWallet?.id,
        walletName: selectedWallet?.description,
        currency: selectedWallet?.currency?.code,
        balance: selectedWallet?.available,
        walletType: SourceWalletTypes.MAIN,
        supportedTransferDestinations: selectedWallet?.supportedTransferDestinations,
        supportsInternalBvnkNetworkTransfers: selectedWallet?.supportsInternalBvnkNetworkTransfers,
        supportsFirstPartyPayouts: selectedWallet?.supportsWithdrawals,
        supportsThirdPartyPayouts: selectedWallet?.supportsThirdParty,
        supportsPayins: selectedWallet?.supportsDeposits,
      } as SourcePayWallet

      setSourceWalletAtom(sourceWallet)
    }
  }, [
    sourceWalletAtom,
    walletAction?.sourceWalletReference,
    walletAction?.meta?.customerWallet?.id,
    activeWallets?.length,
    isFetchingCustomerWallet,
    customerWallet,
  ])

  useEffect(() => {
    if (!sourceWalletAtom || isFetchingWalletFilters) return

    if (sourceWalletAtom?.balance > 0) {
      if (filters?.length) {
        const beneficiariesFilter = getFiatSendBeneficiaryFilters({
          sourceWallet: sourceWalletAtom,
          supportedFilters: filters?.map(filter => filter?.name),
        })
        setBeneficiaryFilters({
          ...beneficiariesFilter,
          beneficiarySearchTerm: debouncedSearchTerm,
        })
      }
    }
  }, [sourceWalletAtom, isFetchingWalletFilters, debouncedSearchTerm])

  const isLoadingWalletOptions = useMemo(() => {
    return isFetchingWallets || isFetchingCustomerWallet || isFetchingCustomerWalletLegacyId
  }, [isFetchingWallets, isFetchingCustomerWallet, isFetchingCustomerWalletLegacyId])

  const handleBeneficiaryFilterChange = useCallback(
    (selectedFilter: string[]) => {
      const beneficiariesFilter = getFiatSendBeneficiaryFilters({
        selectedFilter: selectedFilter?.[0],
        sourceWallet: sourceWalletAtom,
        supportedFilters: filters?.map(filter => filter?.name),
      })
      setBeneficiaryFilters({ ...beneficiariesFilter, beneficiarySearchTerm: debouncedSearchTerm })
    },
    [sourceWalletAtom, filters]
  )

  const handleBeneficiarySelect = useCallback(
    (beneficiary: BeneficiaryDetails) => {
      if (!sourceWalletAtom) {
        setError('sourceWalletLsid', { message: t('wallets.fiatSend.noWalletSelectedError') })
        return
      }
      clearErrors()
      setValue('beneficiaryReference', beneficiary?.reference)
      setValue('currencyCode', beneficiary?.currency)
      setValue('transferDestination', beneficiary?.transferDestination)
      setBeneficiaryAtom(beneficiary)
      setStep(FiatSendSteps.AMOUNT)
    },
    [sourceWalletAtom, setError, clearErrors, setValue, setBeneficiaryAtom, setStep, t]
  )

  const onHandleBeneficiarySearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value)
    },
    [setSearchQuery]
  )

  const handleOnAddBeneficiaryClick = useCallback(() => {
    navigate(PathNames.BANK_BENEFICIARIES)
  }, [navigate])

  const handleWalletOnChange = useCallback(
    (walletId: string) => {
      if (!walletId || !activeWallets?.length) return

      clearErrors('sourceWalletLsid')
      const selectedWallet = activeWallets?.find(wallet => wallet?.lsid === walletId) as WalletType

      const sourceWallet = {
        walletId: selectedWallet?.lsid,
        legacyWalletId: selectedWallet?.id,
        walletName: selectedWallet?.description,
        currency: selectedWallet?.currency?.code,
        balance: selectedWallet?.available,
        walletType: SourceWalletTypes.MAIN,
        supportedTransferDestinations: selectedWallet?.supportedTransferDestinations,
        supportsInternalBvnkNetworkTransfers: selectedWallet?.supportsInternalBvnkNetworkTransfers,
        supportsFirstPartyPayouts: selectedWallet?.supportsWithdrawals,
        supportsThirdPartyPayouts: selectedWallet?.supportsThirdParty,
        supportsPayins: selectedWallet?.supportsDeposits,
      } as SourcePayWallet

      setSourceWalletAtom(sourceWallet)
      setValue('sourceWalletLsid', walletId)
    },
    [activeWallets, clearErrors, setSourceWalletAtom, setValue]
  )

  const filtersDisabled = useMemo(() => {
    return (
      !isLoadingWalletOptions &&
      !isFetchingWalletFilters &&
      !isFetchingBeneficiaries &&
      (!isPayoutsSupported || isInsufficientBalance)
    )
  }, [
    !isLoadingWalletOptions,
    isFetchingWalletFilters,
    isFetchingBeneficiaries,
    isPayoutsSupported,
    isInsufficientBalance,
    fiatBeneficiaries?.length,
  ])

  return (
    <Box paddingT={24} className="max-h-[75vh]">
      {showErrorState && (
        <StatusCard
          customIcon={<Illustration name="CloudLightning" color="error" />}
          headline={t('defaultError')}
        >
          <Description>
            {`${t('requestErrors.comeBackLater')} `}
            <TextAction isWindowReload text={t('requestErrors.refreshPage')} />.
          </Description>
        </StatusCard>
      )}
      {!showErrorState && (
        <Box paddingB={16}>
          <Combobox
            {...register('sourceWalletLsid')}
            key={sourceWalletAtom?.walletId}
            modal
            id="source-wallet"
            testId="source-wallet"
            items={walletsOptions || []}
            label={t('wallets.fiatSend.walletToSendFrom')}
            loading={isLoadingWalletOptions}
            loadingInline={isLoadingWalletOptions}
            onChange={handleWalletOnChange}
            defaultValue={sourceWalletAtom?.walletId}
            errorText={errors?.sourceWalletLsid?.message}
            disabled={isLoadingWalletOptions || !!sourceWalletAtom}
          />

          {sourceWalletAtom && (
            <>
              <Box paddingY={16} width="full" justify="between" flex gapY={12} gapX={8}>
                <Box width="full">
                  <Input
                    placeholderText={t('wallets.fiatSend.search')}
                    onChange={onHandleBeneficiarySearch}
                    searchable={true}
                    testId="fiat-send-beneficiary-search"
                    helperText={t('payouts.beneficiaryFiltersDisclaimer')}
                    disabled={filtersDisabled}
                  />
                </Box>
                <Button
                  testId="add-contact"
                  variant="secondary"
                  onClick={handleOnAddBeneficiaryClick}
                >
                  {t('addNewBeneficiaryWithoutPlus')}
                </Button>
              </Box>

              <BeneficiaryContent
                fiatBeneficiaries={fiatBeneficiaries}
                filters={filters}
                isFetchingBeneficiaries={isFetchingBeneficiaries}
                isFetchingFilters={isFetchingWalletFilters}
                isInsufficientBalance={isInsufficientBalance}
                isPayoutsSupported={isPayoutsSupported}
                sourceWalletAtom={sourceWalletAtom}
                handleBeneficiaryFilterChange={handleBeneficiaryFilterChange}
                handleBeneficiarySelect={handleBeneficiarySelect}
                setSearchQuery={setSearchQuery}
              />
            </>
          )}
        </Box>
      )}
    </Box>
  )
}

export default memo(BeneficiaryForm)
